import React, { useEffect, useState } from 'react';
import { useAuth, authApi, type PropertyOption } from '../lib/auth';

interface Props {
  onClose: () => void;
}

export function SwitchPropertyDialog({ onClose }: Props) {
  const { user, switchProperty } = useAuth();
  const [properties, setProperties] = useState<PropertyOption[]>([]);
  const [selected,   setSelected]   = useState<string>(user?.currentPropertyId ?? '');
  const [loading,    setLoading]    = useState(true);
  const [busy,       setBusy]       = useState(false);
  const [error,      setError]      = useState<string | null>(null);

  // Load the list of properties the user can access
  useEffect(() => {
    (async () => {
      try {
        const list = await authApi.listMyProperties();
        setProperties(list);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load properties.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  async function handleSwitch() {
    if (!selected) return;
    if (selected === user?.currentPropertyId) {
      // No change — just close
      onClose();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await switchProperty(selected);
      // switchProperty triggers window.location.reload() on success;
      // we don't get here unless that fails.
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Switch failed.');
      setBusy(false);
    }
  }

  const currentProperty = properties.find(p => p.id === user?.currentPropertyId);

  return (
    <div className="dialog-overlay" onClick={(e) => {
      // Click-outside dismisses (only on the overlay itself, not the dialog body)
      if (e.target === e.currentTarget && !busy) onClose();
    }}>
      <div className="dialog dialog-switch-property" role="dialog" aria-labelledby="switch-property-title">
        <div className="dialog-titlebar">
          <span id="switch-property-title">Switch Property</span>
          <button type="button" className="dialog-close" onClick={onClose} disabled={busy} aria-label="Close">×</button>
        </div>

        <div className="dialog-body">
          {loading && <div className="dialog-loading">Loading properties…</div>}

          {!loading && error && (
            <div className="dialog-error">{error}</div>
          )}

          {!loading && !error && (
            <>
              <div className="dialog-section">
                <div className="dialog-label">Currently viewing:</div>
                <div className="dialog-current">
                  {currentProperty ? `${currentProperty.name} (${currentProperty.code})` : '(none)'}
                </div>
              </div>

              <div className="dialog-section">
                <div className="dialog-label">Switch to:</div>
                <div className="dialog-property-list">
                  {properties.map(p => (
                    <label key={p.id} className={`dialog-property-item ${selected === p.id ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="property"
                        value={p.id}
                        checked={selected === p.id}
                        onChange={() => setSelected(p.id)}
                        disabled={busy}
                      />
                      <span className="dialog-property-name">{p.name}</span>
                      <span className="dialog-property-code">({p.code})</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="dialog-footer">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSwitch}
            disabled={busy || loading || !selected || selected === user?.currentPropertyId}
          >
            {busy ? 'Switching…' : 'Switch'}
          </button>
        </div>
      </div>
    </div>
  );
}
